import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Divider, Flex, Text } from '@fullstory/components';
import {
  domainsToString,
  pathFromPatternRule,
  APILabel,
  APIPropertyLabels,
} from '@fullstory/library-ui';
import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppDispatch,
  fullstorySelectors,
  getVisiblePageProps,
  getPagePropsLoading,
  SearchMetaAsyncRequestActions,
} from '@fullstory/redux';
import { platformsToLabel } from '@fullstory/util/pages';

const { getOrgId } = fullstorySelectors;

export const PageDefinitionBody: React.FC<{
  pageDefinition: PageDefinition;
  omitDescription?: boolean;
}> = ({ pageDefinition, omitDescription = false }) => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const pagePropsLoading = useSelector(getPagePropsLoading);
  const pageProps = useSelector(getVisiblePageProps);

  const loadConnections = useCallback(() => {
    if (pageDefinition.id) {
      dispatch(
        SearchMetaAsyncRequestActions.getCustomSchemaConnections(orgId, {
          pages: [pageDefinition.id],
        }),
      );
    }
  }, [orgId, pageDefinition.id, dispatch]);

  useEffect(() => {
    loadConnections();
  }, [loadConnections]);

  const { properties, loading, failed } = useMemo(() => {
    if (!pageDefinition.id) {
      return {
        properties: [],
        loading: false,
        failed: false,
      };
    }
    const properties = pageProps.get(pageDefinition.id);
    const loading = pagePropsLoading.has(pageDefinition.id);
    const failed = !properties && !loading;
    return {
      properties: properties ?? [],
      loading,
      failed,
    };
  }, [pageDefinition.id, pagePropsLoading, pageProps]);

  const showProperties = loading || failed || !!properties.length;

  const hasPatterns = pageDefinition.patterns.length > 0;
  const plaformsText = useMemo(
    () => platformsToLabel(pageDefinition.labels, hasPatterns),
    [pageDefinition.labels, hasPatterns],
  );

  const descriptionView = (
    <>
      <Box paddingBottom={8}>
        <Text appearance="bodySmall" color="secondary">
          <FormattedMessage defaultMessage="Description" id="Q8Qw5B" />
        </Text>
      </Box>
      <Box paddingBottom={8}>
        <Text appearance="bodySmall">{pageDefinition.description}</Text>
      </Box>
      <Box padding={[4, 0, 12, 0]}>
        <Divider />
      </Box>
    </>
  );

  return (
    <div>
      {pageDefinition?.description && !omitDescription && descriptionView}
      <Box paddingBottom={8}>
        <Flex justifyContent="space-between">
          <Text appearance="bodySmall" color="secondary">
            <FormattedMessage defaultMessage="Platform" id="Mmuj1R" />
          </Text>
          <Text appearance="bodySmall">{plaformsText}</Text>
        </Flex>
      </Box>
      <Box padding={[4, 0, 12, 0]}>
        <Divider />
      </Box>
      <Box paddingBottom={8}>
        <Text appearance="bodySmall" color="secondary">
          <FormattedMessage defaultMessage="Definition" id="KbSPMS" />
        </Text>
      </Box>
      {pageDefinition.id === 0 && (
        <Box paddingBottom={8}>
          <APILabel
            text={intl.formatMessage({
              defaultMessage: "The URLs in this group haven't been defined as Pages.",
              id: 'FZ37lF',
            })}
          />
        </Box>
      )}
      <Box paddingBottom={8}>
        {pageDefinition.labels.map((label, i) => (
          <React.Fragment key={`definition_name_${label.pageName}`}>
            {i > 0 ? (
              <Box margin={[8, 0]}>
                <Text appearance="bodySmall" color="secondary">
                  <FormattedMessage defaultMessage="or" id="Ntjkqd" />
                </Text>
              </Box>
            ) : null}
            <Flex justifyContent="flex-start" alignItems="flex-start">
              <Box marginRight={8}>
                <APILabel text={intl.formatMessage({ defaultMessage: 'pageName', id: 'WNPdrP' })} />
              </Box>
              <APILabel text={label.pageName} />
            </Flex>
          </React.Fragment>
        ))}
        {pageDefinition.patterns.map((pattern, i) => (
          <React.Fragment key={`definition_pattern_${i}`}>
            {i > 0 || !!pageDefinition.labels.length ? (
              <Box margin={[8, 0]}>
                <Text appearance="bodySmall" color="secondary">
                  <FormattedMessage defaultMessage="or" id="Ntjkqd" />
                </Text>
              </Box>
            ) : null}
            <APILabel text={domainsToString(pattern.domains) + pathFromPatternRule(pattern)} />
          </React.Fragment>
        ))}
      </Box>
      {showProperties && (
        <>
          <Box padding={[4, 0, 12, 0]}>
            <Divider />
          </Box>
          <Box paddingBottom={8}>
            <Text appearance="bodySmall" color="secondary">
              <FormattedMessage defaultMessage="Properties" id="aI80kg" />
            </Text>
          </Box>
          <APIPropertyLabels
            properties={properties}
            loading={loading}
            failed={failed}
            onFailedRetry={loadConnections}
          />
        </>
      )}
    </div>
  );
};
